import type { WeekType } from '@functieloonhuis/shared';

/**
 * Get ISO 8601 week number for a date.
 * Matches the backend logic in workScheduleService.ts.
 */
export function getISOWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

/**
 * Determine week type (A or B) from a date.
 * ADR-002: even ISO week = A, odd = B.
 */
export function getWeekType(date: Date): WeekType {
  return getISOWeekNumber(date) % 2 === 0 ? 'A' : 'B';
}

/**
 * Get the Monday of the week containing the given date.
 */
export function getMondayOfWeek(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

/**
 * Get all 7 dates (Mon-Sun) starting from a Monday.
 */
export function getWeekDates(monday: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

const SHORT_MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const SHORT_DAYS = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];

/**
 * Format a date as "Ma 24 feb".
 */
export function formatDateLabel(date: Date): string {
  const dayName = SHORT_DAYS[date.getDay()];
  const dayNum = date.getDate();
  const month = SHORT_MONTHS[date.getMonth()];
  return `${dayName} ${dayNum} ${month}`;
}

/**
 * Format a date range as "24 feb - 2 mrt 2026".
 */
/** Maandag t/m vrijdag. `Date.getDay()`: 0 = zondag, 6 = zaterdag. */
export function isWerkdag(date: Date): boolean {
  const day = date.getDay();
  return day !== 0 && day !== 6;
}

/** Houdt alleen ma t/m vr over, met behoud van de oorspronkelijke volgorde. */
export function filterWerkdagen(dates: Date[]): Date[] {
  return dates.filter(isWerkdag);
}

/**
 * De werkweek (ma t/m vr) vanaf `monday` — 5 datums.
 *
 * Elk weekoverzicht in de portal gebruikt dit; `getWeekDates` (7 dagen)
 * blijft bestaan voor wie een volledige kalenderweek nodig heeft. Besluit
 * Friso 2026-07-29: het weekend verdwijnt uit álle weekoverzichten. Op
 * productie heeft geen enkele medewerker een weekenddag als werkdag, dus
 * er wordt niets verborgen wat er daadwerkelijk is.
 */
export function getWorkweekDates(monday: Date): Date[] {
  return filterWerkdagen(getWeekDates(monday));
}

/**
 * Format an arbitrary date range as "27 - 31 jul 2026" (or "29 jun - 3 jul
 * 2026" across a month boundary). Extracted from `formatWeekRange` so a
 * view that shows only part of a week can label exactly what it renders.
 */
export function formatDateRange(start: Date, end: Date): string {
  const d1 = start.getDate();
  const m1 = SHORT_MONTHS[start.getMonth()];
  const d2 = end.getDate();
  const m2 = SHORT_MONTHS[end.getMonth()];
  const year = end.getFullYear();

  if (start.getMonth() === end.getMonth()) {
    return `${d1} - ${d2} ${m2} ${year}`;
  }
  return `${d1} ${m1} - ${d2} ${m2} ${year}`;
}

/**
 * Label voor een weekoverzicht: ma t/m vr, want dat is wat die overzichten
 * sinds 2026-07-29 tonen. Liep het tot zondag door, dan beloofde het label
 * twee dagen die nergens in de grid staan.
 */
export function formatWeekRange(monday: Date): string {
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  return formatDateRange(monday, friday);
}

/**
 * Add (or subtract) days from a date.
 */
export function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

/**
 * Add (or subtract) weeks from a date.
 */
export function addWeeks(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n * 7);
  return d;
}

/**
 * Add (or subtract) months from a date.
 */
export function addMonths(date: Date, n: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + n);
  return d;
}

/**
 * Get the first day of the month for a given date.
 */
export function getFirstDayOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/**
 * Get all Mon–Fri workday dates in the given year/month (0-indexed month).
 */
export function getWorkdaysOfMonth(year: number, month: number): Date[] {
  const result: Date[] = [];
  const d = new Date(year, month, 1);
  while (d.getMonth() === month) {
    const day = d.getDay(); // 0=Sun, 6=Sat
    if (day !== 0 && day !== 6) {
      result.push(new Date(d));
    }
    d.setDate(d.getDate() + 1);
  }
  return result;
}

const MONTHS_NL = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december',
];

/**
 * Format a single date as "dinsdag 18 februari 2026".
 */
export function formatDayLabel(date: Date): string {
  const DAYS_FULL = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
  const dayName = DAYS_FULL[date.getDay()];
  const dayNum = date.getDate();
  const month = MONTHS_NL[date.getMonth()];
  const year = date.getFullYear();
  return `${dayName} ${dayNum} ${month} ${year}`;
}

/**
 * Format a month as "februari 2026".
 */
export function formatMonthLabel(date: Date): string {
  return `${MONTHS_NL[date.getMonth()]} ${date.getFullYear()}`;
}

/**
 * Check if two dates are the same calendar day.
 */
export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

/**
 * Format date as "YYYY-MM-DD".
 */
export function toDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
